import { DropdownData } from "@/modules/common/input/dropdown/types/DropdownData";
import { DropDownService } from "@/services/drop-down-data.service";
import { AddOrRemove, FetchCache } from "@/utils/local-storage-cache.util";
import { ActionContext, createStore, Store } from "vuex";

export interface DropDownDataVuex {
  systemParameters: string[] | undefined;
  categories: string[] | undefined;
  languages: string[] | undefined;
  comicTypes: string[] | undefined;
  comicPageMarkerTypes: string[] | undefined;
  lastUpdate: Date | undefined;
  offerorCategories: string[] | undefined;
  userStatusTypes: string[] | undefined;
  userRoles: string[] | undefined;
  acceptorIdType: string[] | undefined;
  presentationType: string[] | undefined
}
const LS_SYSTEM_PARAMETERS_KEY = "systemParameters";
const LS_CATEGORIES_KEY = "categories";
const LS_LANGUAGES_KEY = "languages";
const LS_OFFEROR_CATEGORIES_KEY = "offerorCategories";
const cache = (store: Store<DropDownDataVuex>) => {
  store.subscribe((mutation, state: DropDownDataVuex) => {
    switch (mutation.type) {
      case "setCategories":
        AddOrRemove<string[]>(
          LS_CATEGORIES_KEY,
          state.categories
        );
        break;
      case "setLanguages":
        AddOrRemove<string[]>(
          LS_LANGUAGES_KEY,
          state.languages
        );
        break;
      case "setOfferorCategories":
        AddOrRemove<string[]>(
          LS_OFFEROR_CATEGORIES_KEY,
          state.offerorCategories
        );
        break;
      case "setSystemParameters":
        AddOrRemove<string[] | undefined>(
          LS_OFFEROR_CATEGORIES_KEY,
          state.systemParameters != undefined
            ? state.systemParameters
            : undefined
        );
        break;
      default:
        break;
    }
  });
};

export const DropDownDataStore: Store<DropDownDataVuex> = createStore({
  state: {
    systemParameters: undefined,
    categories: undefined,
    languages: undefined,
    comicTypes: ['CORE', 'SUPPLEMENTARY'],
    comicPageMarkerTypes: ['OFFER', 'SYSTEM'],
    lastUpdate: undefined,
    offerorCategories: undefined,
    userStatusTypes: ['ACTIVE', 'INACTIVE'],
    userRoles: ['ADMIN', 'OFFEROR'],
    acceptorIdType: ['PASSPORT', 'ID'],
    presentationType: ['LOCAL', 'REMOTE']
  },
  mutations: {
    setSystemParameters(state: DropDownDataVuex, systemParameters: string[] | undefined) {
      state.systemParameters = systemParameters;
    },
    setCategories(state: DropDownDataVuex, categories: string[] | undefined) {
      state.categories = categories;
    },
    setLanguages(state: DropDownDataVuex, languages: string[] | undefined) {
      state.languages = languages;
    },
    setOfferorCategories(
      state: DropDownDataVuex,
      offerorCategories: string[] | undefined
    ) {
      state.offerorCategories = offerorCategories;
    },
    setLastUpdate(state: DropDownDataVuex) {
      state.lastUpdate = new Date();
    },
  },
  actions: {
    async fetchLanguage(
      context: ActionContext<DropDownDataVuex, DropDownDataVuex>
    ) {
      const checkLastUpdate =
        context.state.lastUpdate &&
        (new Date().getTime() -
          context.state.lastUpdate.getTime() / 1000 / 60 <=
          process.env.VUE_APP_UPDATE_DROP_DOWN_DATA_MINUTE ??
          -1);
      const data = FetchCache<string[] | undefined>(LS_LANGUAGES_KEY);
      if (checkLastUpdate && data) {
        context.commit("setLanguages", data);
        return;
      }
      const service = new DropDownService();
      await service
        .ComicLanguages()
        .then((res) => context.commit("setLanguages", res))
        .catch((e) => {
          throw e;
        });
      context.commit("setLastUpdate");
      return;
    },
    async fetchCategories(
      context: ActionContext<DropDownDataVuex, DropDownDataVuex>
    ) {
      const checkLastUpdate =
        context.state.lastUpdate &&
        (new Date().getTime() -
          context.state.lastUpdate.getTime() / 1000 / 60 <=
          process.env.VUE_APP_UPDATE_DROP_DOWN_DATA_MINUTE ??
          -1);
      const data = FetchCache<string[] | undefined>(LS_CATEGORIES_KEY);
      if (checkLastUpdate && data) {
        context.commit("setCategories", data);
        return;
      }
      const service = new DropDownService();
      await service
        .ComicCategories()
        .then((res) => context.commit("setCategories", res))
        .catch((e) => {
          throw e;
        });
      context.commit("setLastUpdate");
      return;
    },
    async fetchOfferorCategories(
      context: ActionContext<DropDownDataVuex, DropDownDataVuex>
    ) {
      const checkLastUpdate =
        context.state.lastUpdate &&
        (new Date().getTime() -
          context.state.lastUpdate.getTime() / 1000 / 60 <=
          process.env.VUE_APP_UPDATE_DROP_DOWN_DATA_MINUTE ??
          -1);
      const data = FetchCache<string[] | undefined>(LS_OFFEROR_CATEGORIES_KEY);
      if (checkLastUpdate && data) {
        context.commit("setOfferorCategories", data);
        return;
      }
      const service = new DropDownService();
      await service.OfferorCategories()
        .then((res) => context.commit("setOfferorCategories", res))
        .catch((e) => {
          throw e;
        });
      context.commit("setLastUpdate");
      return;
    },
    async fetchSystemParameters(
      context: ActionContext<DropDownDataVuex, DropDownDataVuex>
    ) {
      const checkLastUpdate =
        context.state.lastUpdate &&
        (new Date().getTime() -
          context.state.lastUpdate.getTime() / 1000 / 60 <=
          process.env.VUE_APP_UPDATE_DROP_DOWN_DATA_MINUTE ??
          -1);
      const data = FetchCache<string[] | undefined>(LS_SYSTEM_PARAMETERS_KEY);
      if (checkLastUpdate && data) {
        context.commit("setSystemParameters", data);
        return;
      }
      const service = new DropDownService();
      await service
        .SystemParameters()
        .then((res) => context.commit("setSystemParameters", res))
        .catch((e) => {
          throw e;
        });
      context.commit("setLastUpdate");
      return;
    },
  },
  getters: {
    Categories: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.categories);
    },
    Languages: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.languages);
    },
    ComicTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.comicTypes);
    },
    UserStatusTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.userStatusTypes);
    },
    UserRoleTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.userRoles);
    },
    AcceptorIdTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.acceptorIdType);
    },
    PresentationTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.presentationType);
    },
    ComicPageMarkerTypes: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.comicPageMarkerTypes);
    },
    offerorCategories:
      (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
        return StringArrayToDropDownData(state.offerorCategories);
      },
    SystemParameters: (state: DropDownDataVuex) => (): DropdownData[] | undefined => {
      return StringArrayToDropDownData(state.systemParameters);
    },
  },
  plugins: [cache],
});

const StringArrayToDropDownData = (
  arr: string[] | undefined
): DropdownData[] => {
  // eslint-disable-next-line prefer-const
  let dropdowndata: DropdownData[] = [];
  if (!arr) {
    return dropdowndata;
  }
  for (let index = 0; index < arr.length; index++) {
    const element = arr[index];
    dropdowndata.push({
      id: index,
      name: element,
      value: element
    });
  }
  return dropdowndata;
};
